import { Theme } from '@logto/schemas';

export const themePreferenceStorageKey = 'logto-theme-preference';

export const systemThemePreference = 'system' as const;

export type ThemePreference = Theme | typeof systemThemePreference;

const prefersDarkSchemeQuery = '(prefers-color-scheme: dark)';

const getDarkThemeWatchMedia = (): MediaQueryList | undefined => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return undefined;
  }

  return window.matchMedia(prefersDarkSchemeQuery);
};

export const getThemeBySystemConfiguration = (): Theme =>
  getDarkThemeWatchMedia()?.matches ? Theme.Dark : Theme.Light;

export const getStoredThemePreference = (): ThemePreference => {
  if (typeof window === 'undefined') {
    return systemThemePreference;
  }

  try {
    const preference = window.localStorage.getItem(themePreferenceStorageKey);

    if (preference === Theme.Light || preference === Theme.Dark) {
      return preference;
    }
  } catch {
    // Storage may be unavailable in private browsing or embedded contexts.
  }

  return systemThemePreference;
};

export const getThemeByPreference = (preference: ThemePreference): Theme =>
  preference === systemThemePreference ? getThemeBySystemConfiguration() : preference;

export const persistThemePreference = (preference: ThemePreference) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (preference === systemThemePreference) {
      window.localStorage.removeItem(themePreferenceStorageKey);
    } else {
      window.localStorage.setItem(themePreferenceStorageKey, preference);
    }
  } catch {
    // The in-memory state still applies when storage is unavailable.
  }
};

export const subscribeToSystemTheme = (listener: () => void) => {
  const media = getDarkThemeWatchMedia();

  if (!media) {
    return () => undefined;
  }

  media.addEventListener('change', listener);

  return () => {
    media.removeEventListener('change', listener);
  };
};
